import { getCartErrorMessage } from '@theme/cart-feedback';

// "Fancy a little extra?" checkout pop-up. See snippets/checkout-upsell.liquid.
const SESSION_KEY = 'sweetzone:checkout-upsell-shown';
const MAX_ITEMS = 3;
const LOAD_TIMEOUT_MS = 3500;

let shownInMemory = false;

function hasShownThisSession() {
  if (shownInMemory) return true;
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markShownThisSession() {
  shownInMemory = true;
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage can be blocked; the in-memory flag still covers this page view.
  }
}

async function getJSON(url, signal) {
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

// Mirrors snippets/product-weight.liquid: show the variant weight only when the
// title does not already carry a weight such as "170g" or "1kg".
function titleHasWeight(title) {
  return String(title || '')
    .toLowerCase()
    .split(' ')
    .some((word) => /^[0-9]/.test(word) && word.endsWith('g'));
}

// Round-robin across cart items so suggestions relate to more than one of them.
function interleave(lists) {
  const merged = [];
  const longest = Math.max(0, ...lists.map((list) => list.length));
  for (let index = 0; index < longest; index += 1) {
    for (const list of lists) {
      if (list[index]) merged.push(list[index]);
    }
  }
  return merged;
}

// Only single-variant, in-stock, one-off purchase products are offered, so the
// pop-up never chooses an option or a subscription on the customer's behalf.
function addableVariant(product) {
  if (!product?.available || product.requires_selling_plan) return null;
  if (String(product.type || '').toLowerCase().includes('gift card')) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  if (variants.length !== 1) return null;
  const variant = variants[0];
  if (!variant?.available || variant.requires_selling_plan) return null;
  return variant;
}

function sizedImageUrl(src, width) {
  if (!src) return '';
  try {
    const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return '';
  }
}

class SCheckoutUpsell extends HTMLElement {
  #ac;
  #loadController = null;
  #dialog;
  #list;
  #status;
  #total;
  #totalRow;
  #template;
  #pending = null;
  #bypass = false;
  #loading = false;
  #adding = false;
  #proceeding = false;
  #cartChanged = false;
  #currency = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#list = this.querySelector('[data-upsell-list]');
    this.#status = this.querySelector('[data-upsell-status]');
    this.#total = this.querySelector('[data-upsell-total]');
    this.#totalRow = this.querySelector('[data-upsell-total-row]');
    this.#template = this.querySelector('template[data-upsell-item]');
    if (!this.#dialog || !this.#list || !this.#template) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Capture phase so the decision is made before the native cart form submits.
    document.addEventListener('submit', (event) => this.#onSubmit(event), { ...opts, capture: true });
    this.querySelector('[data-upsell-close]')?.addEventListener('click', () => this.#dialog.close(), opts);
    this.querySelector('[data-upsell-continue]')?.addEventListener('click', () => this.#continue(), opts);
    this.#list.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
      if (button) this.#add(button);
    }, opts);
    this.#dialog.addEventListener('close', () => this.#onClose(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#loadController?.abort();
  }

  #onSubmit(event) {
    if (this.#bypass || event.defaultPrevented) return;

    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || !(submitter instanceof HTMLElement)) return;
    if (submitter.getAttribute('name') !== 'checkout') return;

    // Only the cart page and cart drawer Checkout buttons.
    const cartRoot = form.closest('s-cart');
    if (!cartRoot) return;

    if (hasShownThisSession()) return;
    if (!window.Shopify?.actions?.updateCart) return;

    event.preventDefault();
    if (this.#loading) return;

    this.#pending = { form, submitter, cartId: cartRoot.id || '' };
    this.#open(submitter);
  }

  async #open(submitter) {
    this.#loading = true;
    this.#setBusy(submitter, true);
    this.#loadController = new AbortController();
    const { signal } = this.#loadController;
    const timer = window.setTimeout(() => this.#loadController?.abort(), LOAD_TIMEOUT_MS);

    let picks = [];
    try {
      picks = await this.#loadRecommendations(signal);
    } catch {
      picks = [];
    } finally {
      window.clearTimeout(timer);
      this.#loadController = null;
      this.#loading = false;
      this.#setBusy(submitter, false);
    }

    if (!picks.length) {
      this.#proceed();
      return;
    }

    try {
      this.#render(picks);
      markShownThisSession();
      this.#dialog.showModal();
    } catch {
      this.#proceed();
    }
  }

  async #loadRecommendations(signal) {
    const cart = await getJSON(`${this.dataset.cartUrl}.js`, signal);
    this.#currency = cart?.currency || '';
    this.#showTotal(cart);

    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length || !this.#currency) return [];

    const inCart = new Set(items.map((item) => String(item.product_id)));
    // cart.js lists the most recently added lines first.
    const sources = [...inCart].slice(0, MAX_ITEMS);

    const fetchIntent = (intent, limit) => Promise.all(sources.map((productId) => {
      const params = new URLSearchParams({ product_id: productId, limit: String(limit), intent });
      return getJSON(`${this.dataset.recommendationsUrl}.json?${params}`, signal)
        .then((data) => (Array.isArray(data?.products) ? data.products : []))
        .catch((error) => {
          if (signal.aborted) throw error;
          return [];
        });
    }));

    // Complementary picks (set in Search & Discovery) come first, then related products.
    const [complementary, related] = await Promise.all([
      fetchIntent('complementary', 4),
      fetchIntent('related', 6),
    ]);

    const picks = [];
    const seen = new Set(inCart);
    for (const product of [...interleave(complementary), ...interleave(related)]) {
      if (picks.length >= MAX_ITEMS) break;
      const id = String(product?.id || '');
      if (!id || seen.has(id)) continue;
      const variant = addableVariant(product);
      if (!variant) continue;
      seen.add(id);
      picks.push({ product, variant });
    }
    return picks;
  }

  #render(picks) {
    this.#status.textContent = '';
    this.#list.replaceChildren(...picks.map(({ product, variant }) => this.#renderItem(product, variant)));
  }

  #renderItem(product, variant) {
    const item = this.#template.content.firstElementChild.cloneNode(true);
    const title = String(product.title || '');

    const image = item.querySelector('[data-upsell-image]');
    const imageSrc = product.featured_image || variant.featured_image?.src || '';
    const imageUrl = sizedImageUrl(imageSrc, 160);
    if (imageUrl) {
      // Load straight away: iPhone Safari can skip lazy images that appear inside a pop-up, leaving blank squares.
      image.loading = 'eager';
      image.src = imageUrl;
    } else {
      image.remove();
    }

    item.querySelector('[data-upsell-title]').textContent = title;
    item.querySelector('[data-upsell-weight]').textContent = this.#weightText(title, variant.weight);
    item.querySelector('[data-upsell-price]').textContent = this.#formatMoney(variant.price);

    const button = item.querySelector('[data-upsell-add]');
    const minimum = Number.parseInt(variant.quantity_rule?.min, 10);
    button.textContent = this.dataset.addLabel || '';
    button.setAttribute('aria-label', this.#withTitle(this.dataset.addAria, title));
    button.dataset.variantId = String(variant.id);
    button.dataset.productId = String(product.id);
    button.dataset.quantity = String(Number.isFinite(minimum) && minimum > 0 ? minimum : 1);
    button.dataset.title = title;
    return item;
  }

  async #add(button) {
    if (this.#adding || button.getAttribute('aria-disabled') === 'true') return;

    const item = button.closest('li');
    const error = item?.querySelector('[data-upsell-error]');
    const title = button.dataset.title || '';

    // Only the tapped button changes while adding; the other rows stay as they are so nothing flickers.
    this.#adding = true;
    item?.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingLabel || '';
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    try {
      // Add quietly while the pop-up is open. Going through the theme's cart action would
      // re-render the cart drawer behind the pop-up inside a full-screen fade, which makes
      // the whole screen flicker on phones. The drawer refreshes once the pop-up closes.
      const response = await fetch(`${this.dataset.cartUrl}/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: Number(button.dataset.quantity) || 1 }],
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(String(data?.description || data?.message || ''));

      this.#cartChanged = true;
      button.dataset.added = 'true';
      button.textContent = this.dataset.addedLabel || '';
      const addedMessage = this.#withTitle(this.dataset.addedStatus, title);
      button.setAttribute('aria-label', addedMessage);
      this.#status.textContent = addedMessage;
      this.#refreshTotal();
    } catch (caught) {
      button.textContent = this.dataset.addLabel || '';
      if (error) {
        error.textContent = (caught instanceof Error && caught.message && !/^Request failed/.test(caught.message)
          ? caught.message
          : getCartErrorMessage(caught)) || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      item?.removeAttribute('aria-busy');
      button.removeAttribute('aria-busy');
      this.#adding = false;
      this.#setAddButtonsDisabled(false);
    }
  }

  #setAddButtonsDisabled(disabled) {
    for (const button of this.#list.querySelectorAll('[data-upsell-add]')) {
      if (disabled || button.dataset.added === 'true') {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }
  }

  async #refreshTotal() {
    try {
      this.#showTotal(await getJSON(`${this.dataset.cartUrl}.js`));
    } catch {
      // The cart drawer and cart page still refresh through Standard Actions.
    }
  }

  #showTotal(cart) {
    if (!this.#total || !this.#totalRow || !cart?.currency) return;
    this.#currency = cart.currency;
    const next = this.#formatMoney(cart.total_price);
    const changed = !this.#totalRow.hidden && this.#total.textContent !== next;
    this.#total.textContent = next;
    this.#totalRow.hidden = false;
    if (changed) {
      // A gentle pop so the new total is noticed without anything jumping.
      this.#total.classList.remove('is-updated');
      void this.#total.offsetWidth;
      this.#total.classList.add('is-updated');
    }
  }

  #continue() {
    if (this.#adding) return;
    this.#proceeding = true;
    this.#dialog.close();
    this.#proceed();
  }

  // Resubmit the original cart form so notes, quantities and discounts post
  // exactly as they would without the pop-up. Falls back to /checkout.
  #proceed() {
    const pending = this.#pending;
    this.#pending = null;

    let submitter = pending?.submitter;
    let form = pending?.form;
    if (!submitter?.isConnected || !form?.isConnected) {
      submitter = this.#currentCheckoutButton(pending?.cartId);
      form = submitter?.form || null;
    }

    if (form && submitter) {
      this.#bypass = true;
      try {
        form.requestSubmit(submitter);
        return;
      } catch {
        // Fall through to the direct checkout URL.
      } finally {
        this.#bypass = false;
      }
    }

    const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
    window.location.assign(`${root}checkout`);
  }

  #currentCheckoutButton(cartId) {
    const root = cartId ? document.getElementById(cartId) : null;
    const button = root?.querySelector('[name="checkout"]') || document.querySelector('s-cart [name="checkout"]');
    return button instanceof HTMLButtonElement || button instanceof HTMLInputElement ? button : null;
  }

  #onClose() {
    if (this.#proceeding) {
      this.#proceeding = false;
      this.#cartChanged = false;
      return;
    }

    // Shopper closed the pop-up after adding extras: bring the drawer, cart page and header count up to date now.
    if (this.#cartChanged) {
      this.#cartChanged = false;
      customElements.whenDefined('s-cart')
        .then(() => customElements.get('s-cart')?.refreshMountedCarts?.())
        .catch(() => {});
    }

    const cartId = this.#pending?.cartId;
    this.#pending = null;
    // s-dialog restores focus to the trigger; if the cart re-rendered it, use the new button.
    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && active.isConnected) return;
      this.#currentCheckoutButton(cartId)?.focus();
    });
  }

  #setBusy(element, busy) {
    if (!(element instanceof HTMLElement)) return;
    if (busy) {
      element.setAttribute('aria-busy', 'true');
      element.classList.add('cursor-wait');
    } else {
      element.removeAttribute('aria-busy');
      element.classList.remove('cursor-wait');
    }
  }

  #weightText(title, grams) {
    const weight = Number(grams) || 0;
    if (weight <= 0 || titleHasWeight(title)) return '';
    if (weight >= 1000) {
      const kg = String(Math.round(weight / 100) / 10).replace(/\.0$/, '');
      return ` ${(this.dataset.weightKg || '__W__kg').replace('__W__', kg)}`;
    }
    return ` ${(this.dataset.weightG || '__W__g').replace('__W__', String(weight))}`;
  }

  #withTitle(template, title) {
    return String(template || '').replace('__TITLE__', title);
  }

  #formatMoney(cents) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    const locale = document.documentElement.lang || this.dataset.locale || undefined;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: this.#currency }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('s-checkout-upsell')) {
  customElements.define('s-checkout-upsell', SCheckoutUpsell);
}
